import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from "react";

const STORAGE_KEY = "proof:cart:v1";

const CartContext = createContext(null);

const loadInitial = () => {
    if (typeof window === "undefined") return { items: [] };
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return { items: [] };
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.items)) return parsed;
        return { items: [] };
    } catch (e) {
        return { items: [] };
    }
};

const lineKey = (it) => `${it.id}-${it.size}`;

const reducer = (state, action) => {
    switch (action.type) {
        case "add": {
            const incoming = action.payload;
            const idx = state.items.findIndex(
                (i) => lineKey(i) === lineKey(incoming),
            );
            if (idx >= 0) {
                const items = state.items.slice();
                items[idx] = {
                    ...items[idx],
                    qty: items[idx].qty + (incoming.qty || 1),
                };
                return { ...state, items };
            }
            return {
                ...state,
                items: [...state.items, { ...incoming, qty: incoming.qty || 1 }],
            };
        }
        case "update": {
            const { id, size, qty } = action.payload;
            const items = state.items
                .map((i) =>
                    i.id === id && i.size === size ? { ...i, qty } : i,
                )
                .filter((i) => i.qty > 0);
            return { ...state, items };
        }
        case "remove": {
            const { id, size } = action.payload;
            return {
                ...state,
                items: state.items.filter(
                    (i) => !(i.id === id && i.size === size),
                ),
            };
        }
        case "clear":
            return { ...state, items: [] };
        case "hydrate":
            return action.payload;
        default:
            return state;
    }
};

export const CartProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, undefined, loadInitial);

    // Persist
    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch (e) {
            /* ignore quota errors */
        }
    }, [state]);

    const addItem = useCallback((item) => {
        dispatch({ type: "add", payload: item });
        if (typeof window !== "undefined") {
            window.dispatchEvent(
                new CustomEvent("proof:cart-add", { detail: item }),
            );
        }
    }, []);

    const updateQty = useCallback((id, size, qty) => {
        dispatch({ type: "update", payload: { id, size, qty } });
    }, []);

    const removeItem = useCallback((id, size) => {
        dispatch({ type: "remove", payload: { id, size } });
    }, []);

    const clearCart = useCallback(() => {
        dispatch({ type: "clear" });
    }, []);

    // Listen to legacy `proof:cart-add` CustomEvent — older components emit it directly
    useEffect(() => {
        const onLegacy = (e) => {
            if (!e?.detail || e.detail.__internal) return;
            const d = e.detail;
            // Skip if no id (event already dispatched by addItem internally)
            if (typeof d.id === "undefined" || typeof d.size === "undefined") return;
            if (d.__fromAdd) return;
        };
        window.addEventListener("proof:cart-add", onLegacy);
        return () => window.removeEventListener("proof:cart-add", onLegacy);
    }, []);

    const value = useMemo(() => {
        const count = state.items.reduce((a, i) => a + i.qty, 0);
        const subtotal = state.items.reduce(
            (a, i) => a + i.qty * (i.priceEUR || 0),
            0,
        );
        return {
            items: state.items,
            count,
            subtotal,
            addItem,
            updateQty,
            removeItem,
            clearCart,
        };
    }, [state.items, addItem, updateQty, removeItem, clearCart]);

    return (
        <CartContext.Provider value={value}>{children}</CartContext.Provider>
    );
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used inside CartProvider");
    return ctx;
};
